import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'

/** Resumen de un workflow tal y como lo devuelve el backend (/api/workflows). */
export interface WorkflowResumen {
  id: string
  nombre: string
  activo: boolean
  actualizadoEn: string | null
  tags: string[]
}

/** Resumen de una ejecución de un workflow. */
export interface EjecucionResumen {
  id: string
  workflowId: string
  estado: string
  modo: string
  iniciadaEn: string | null
  finalizadaEn: string | null
}

/** Lista todos los workflows con su estado (activos primero, los ordena la API). */
export function useWorkflows() {
  const api = useApi()
  return useQuery<WorkflowResumen[], ErrorApi>({
    queryKey: ['workflows'],
    queryFn: ({ signal }) => api.pedir<WorkflowResumen[]>('/api/workflows', { senal: signal }),
  })
}

/** Últimas ejecuciones de un workflow. Solo se activa cuando hay un id. */
export function useEjecuciones(workflowId: string | null) {
  const api = useApi()
  return useQuery<EjecucionResumen[], ErrorApi>({
    queryKey: ['workflows', workflowId, 'ejecuciones'],
    enabled: workflowId !== null,
    queryFn: ({ signal }) =>
      api.pedir<EjecucionResumen[]>(`/api/workflows/${workflowId}/ejecuciones`, {
        parametros: { limite: 20 },
        senal: signal,
      }),
  })
}

/**
 * Activa o desactiva un workflow. Al terminar invalida la lista para que el
 * estado de la píldora refleje el real de n8n sin recargar la página.
 */
export function useCambiarEstadoWorkflow() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<WorkflowResumen, ErrorApi, { id: string; activo: boolean }>({
    mutationFn: ({ id, activo }) =>
      api.pedir<WorkflowResumen>(`/api/workflows/${id}/estado`, {
        metodo: 'POST',
        cuerpo: { activo },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['workflows'] })
    },
  })
}
